import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { MessageSquare, Paperclip, ChevronDown, ChevronUp, User, Clock } from 'lucide-react';

const CounselorFeedbackList = ({ feedback, currentUserId, onMarkRead }) => {
  const [expandedId, setExpandedId] = useState(null);
  const [showReportDialog, setShowReportDialog] = useState(null);

  if (!feedback || feedback.length === 0) {
    return (
      <Card className="p-8 text-center bg-white/90" data-testid="no-feedback">
        <MessageSquare className="w-10 h-10 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">No counselor feedback received yet</p>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="feedback-list">
      {feedback.map((fb) => {
        const isRead = fb.read_by?.includes(currentUserId);
        const isExpanded = expandedId === fb.id;

        return (
          <Card
            key={fb.id}
            className={`p-4 transition-all ${!isRead ? 'border-l-4 border-l-indigo-500 bg-indigo-50/50' : 'bg-white/90'}`}
            data-testid={`feedback-${fb.id}`}
          >
            <div
              className="flex justify-between items-start cursor-pointer"
              onClick={() => {
                setExpandedId(isExpanded ? null : fb.id);
                if (!isRead && onMarkRead) onMarkRead(fb.id);
              }}
            >
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <User className="w-4 h-4 text-indigo-600" />
                  <span className="font-semibold text-sm text-gray-900">{fb.counselor_name}</span>
                  {!isRead && <Badge className="bg-indigo-500 text-white text-xs">New</Badge>}
                  {fb.attached_report && <Badge variant="outline" className="text-xs"><Paperclip className="w-3 h-3 mr-1" />Report</Badge>}
                </div>
                <p className="text-sm text-gray-600 mb-1">
                  Re: <span className="font-medium">{fb.student_name}</span>
                  {fb.student_grade && <span className="text-gray-400 ml-1">({fb.student_grade})</span>}
                </p>
                <p className={`text-sm text-gray-700 ${!isExpanded ? 'line-clamp-2' : ''}`}>{fb.feedback_text}</p>
              </div>
              <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                <span className="text-xs text-gray-400 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {new Date(fb.created_at).toLocaleDateString()}
                </span>
                {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
              </div>
            </div>

            {isExpanded && (
              <div className="mt-3 pt-3 border-t space-y-3">
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{fb.feedback_text}</p>

                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span>Sent to:</span>
                  {fb.sent_to_student && <Badge variant="outline" className="text-xs">Student</Badge>}
                  {fb.sent_to_parent && <Badge variant="outline" className="text-xs">Parent</Badge>}
                  {fb.sent_to_teacher && <Badge variant="outline" className="text-xs">Teacher</Badge>}
                </div>

                {fb.attached_report && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs"
                    onClick={(e) => { e.stopPropagation(); setShowReportDialog(fb.id); }}
                    data-testid={`view-report-${fb.id}`}
                  >
                    <Paperclip className="w-3 h-3 mr-1" /> View Attached Report
                  </Button>
                )}
              </div>
            )}

            {/* Report Dialog */}
            {showReportDialog === fb.id && fb.attached_report && (
              <Dialog open={true} onOpenChange={() => setShowReportDialog(null)}>
                <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader><DialogTitle>Attached Report</DialogTitle></DialogHeader>
                  <div className="space-y-3 py-2">
                    {fb.attached_report.report_title && (
                      <h3 className="font-semibold text-lg">{fb.attached_report.report_title}</h3>
                    )}
                    {fb.attached_report.executive_summary && (
                      <div><p className="text-xs font-semibold text-gray-500">Executive Summary</p><p className="text-sm text-gray-700">{fb.attached_report.executive_summary}</p></div>
                    )}
                    {fb.attached_report.key_findings?.length > 0 && (
                      <div><p className="text-xs font-semibold text-gray-500">Key Findings</p><ul className="text-sm space-y-1">{fb.attached_report.key_findings.map((f, i) => <li key={i}>- {f}</li>)}</ul></div>
                    )}
                    {fb.attached_report.student_analyses?.map((sa, idx) => (
                      <Card key={idx} className="p-3 border">
                        <p className="font-semibold text-sm">{sa.student_name} — <Badge className={sa.risk_level === 'high' ? 'bg-red-100 text-red-700' : sa.risk_level === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}>{sa.risk_level}</Badge></p>
                        {sa.recommendations?.map((r, i) => <p key={i} className="text-xs text-gray-600 ml-2">- {r}</p>)}
                      </Card>
                    ))}
                    {fb.attached_report.recommendations_for_counselor?.length > 0 && (
                      <div><p className="text-xs font-semibold text-gray-500">Recommendations</p><ul className="text-sm space-y-1">{fb.attached_report.recommendations_for_counselor.map((r, i) => <li key={i}>- {r}</li>)}</ul></div>
                    )}
                    {/* Fallback: show raw if not structured */}
                    {!fb.attached_report.report_title && !fb.attached_report.executive_summary && (
                      <pre className="text-xs bg-gray-50 p-3 rounded overflow-auto max-h-96">{JSON.stringify(fb.attached_report, null, 2)}</pre>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </Card>
        );
      })}
    </div>
  );
};

export default CounselorFeedbackList;
